import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import { Button, Dialog, DialogContent, DialogHeaderWithClose, DialogTitle } from '@ideoz/ui';
import type { Integration } from '../data/catalog';

interface IntegrationInstructionsDialogProps {
  /** The integration whose instructions to show; dialog is open while non-null. */
  integration: Integration | null;
  onClose: () => void;
}

/** A fixed value (the callback URL) shown with a copy button. */
function CopyRow({ value }: { value: string }): JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  };

  return (
    <div className="mt-2 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/60">
      <code className="min-w-0 flex-1 break-all text-xs text-gray-700 dark:text-gray-200">
        {value}
      </code>
      <button
        type="button"
        onClick={copy}
        aria-label={t('integrations.instructions.copy')}
        title={t('integrations.instructions.copy')}
        data-testid="integration-instructions-copy"
        className="flex-shrink-0 rounded p-1 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
      >
        {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
      </button>
    </div>
  );
}

/**
 * Small dialog that walks the founder through the manual setup steps for a tool
 * that isn't pure one-click OAuth. Steps render as HTML (so a step can carry a
 * link that opens in a new tab), and a step can show a fixed value (the callback
 * URL) with a copy button. Content is authored in the catalog, not user input.
 */
export function IntegrationInstructionsDialog({
  integration,
  onClose,
}: IntegrationInstructionsDialogProps): JSX.Element | null {
  const { t } = useTranslation();
  const instructions = integration?.instructions;
  if (!integration || !instructions) return null;

  return (
    <Dialog open={!!integration} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeaderWithClose>
          <DialogTitle>
            {t('integrations.instructions.title', { name: integration.name })}
          </DialogTitle>
        </DialogHeaderWithClose>

        <div className="flex flex-col gap-4 px-6">
          {instructions.intro && (
            <p className="text-sm text-gray-600 dark:text-gray-300">{instructions.intro}</p>
          )}

          <ol className="flex flex-col gap-3">
            {instructions.steps.map((step, i) => {
              const { html, copy } =
                typeof step === 'string' ? { html: step, copy: undefined } : step;
              return (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700 dark:bg-primary-500/15 dark:text-primary-300">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <span
                      className="text-sm text-gray-700 [&_a]:font-medium [&_a]:text-primary-600 [&_a]:underline dark:text-gray-200 dark:[&_a]:text-primary-300"
                      // Authored in the catalog (static), not user input.
                      dangerouslySetInnerHTML={{ __html: html }}
                    />
                    {copy && <CopyRow value={copy} />}
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="flex items-center justify-end gap-3 py-2">
            <Button variant="primary" onClick={onClose}>
              {t('integrations.instructions.gotIt')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default IntegrationInstructionsDialog;
